import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const OurWorkPage = ({ siteSettings, projects = [] }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const handlePrevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  // Project Modal Component (Fully Responsive)
  const ProjectModal = ({ project }) => {
    const images = project.images || [project.imageUrl];
    const currentImage = images[currentImageIndex];

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-3 sm:p-6 flex items-center justify-between gap-2">
            <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-800 truncate">{project.title}</h2>
            <button
              onClick={handleCloseModal}
              className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
            >
              <X size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
            {/* Image Gallery with Carousel */}
            <div>
              <div className="relative bg-gray-200 rounded-lg overflow-hidden">
                {/* Main Image */}
                <div className="relative w-full h-0 pb-[66.67%]">
                  <img
                    src={currentImage}
                    alt={`${project.title} - ${currentImageIndex + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/800x600/D4BBAA/333333?text=Image+Not+Found';
                    }}
                  />
                </div>

                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-orange-600 hover:bg-orange-700 text-white p-1.5 sm:p-2 rounded-full shadow-lg transition z-10"
                    >
                      <ChevronLeft size={18} className="sm:w-6 sm:h-6" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-orange-600 hover:bg-orange-700 text-white p-1.5 sm:p-2 rounded-full shadow-lg transition z-10"
                    >
                      <ChevronRight size={18} className="sm:w-6 sm:h-6" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-black/60 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </div>

              {/* Slide Indicators */}
              {images.length > 1 && (
                <div className="flex justify-center gap-1.5 sm:gap-2 mt-4 sm:mt-6">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      className={`h-2 sm:h-3 rounded-full transition-all ${
                        index === currentImageIndex
                          ? 'bg-orange-600 w-6 sm:w-8'
                          : 'bg-gray-300 w-2 sm:w-3 hover:bg-orange-400'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Project Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 pt-4 sm:pt-6 border-t border-gray-200">
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">Project Type</h3>
                <p className="text-base sm:text-lg font-semibold text-gray-800 capitalize">{project.project_type}</p>
              </div>
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">Location</h3>
                <p className="text-base sm:text-lg font-semibold text-gray-800">{project.location}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">Project Description</h3>
              <p className="text-xs sm:text-base text-gray-600 leading-relaxed">{project.description}</p>
            </div>

            {/* Call to Action */}
            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <a
                href={siteSettings.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white text-sm sm:text-base font-semibold rounded-lg transition block text-center"
              >
                Inquire About Similar Projects
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-10 sm:mb-14 md:mb-16 lg:mb-20">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-2 sm:mb-4">
            Our Work
          </h1>
          <p className="text-xs sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Discover how we've transformed spaces with carefully curated artwork collections for luxury villas, boutique hotels, and residential properties.
          </p>
        </div>

        {/* Projects Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {(projects || []).map((project) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className="group bg-white rounded-lg sm:rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden h-48 sm:h-56 md:h-64 bg-gray-200">
                <img
                  src={project.imageUrl}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/400x300/D4BBAA/333333?text=Project+Image';
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 sm:p-4">
                  <p className="text-white text-sm sm:text-base font-semibold flex items-center">
                    View Project Details →
                  </p>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-3 sm:p-4 md:p-6">
                <h3 className="text-base sm:text-lg md:text-xl font-serif font-bold text-gray-800 mb-1 sm:mb-2 group-hover:text-orange-600 transition line-clamp-2">
                  {project.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm flex-wrap">
                  <span className="inline-block px-2 sm:px-3 py-1 bg-orange-50 text-orange-800 rounded-full font-medium capitalize">
                    {project.project_type}
                  </span>
                  <span className="font-semibold text-gray-700">{project.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {(!projects || projects.length === 0) && (
          <div className="text-center py-16 sm:py-20">
            <p className="text-base sm:text-lg text-gray-600">No projects available at the moment.</p>
          </div>
        )}
      </div>

      {/* Project Modal */}
      {selectedProject && <ProjectModal project={selectedProject} />}
    </div>
  );
};

export default OurWorkPage;